import { createSlice } from '@reduxjs/toolkit';
import {
  registerUser,
  loginUser,
  verifyEmailUser,
  resendVerificationUser,
  forgotPasswordUser,
  resetPasswordUser,
  getProfile,
  updateProfile,
  changePasswordUser,
  logoutUser,
} from '../../thunks/authThunk';

const initialState = {
  user: null,
  access_token: null,
  refresh_token: null,
  loading: false,
  error: null,
  message: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    clearMessage(state) {
      state.message = null;
    },
    setTokens(state, action) {
      state.access_token = action.payload?.access || state.access_token;
      state.refresh_token = action.payload?.refresh || state.refresh_token;
    },
    clearAuth(state) {
      state.user = null;
      state.access_token = null;
      state.refresh_token = null;
      state.error = null;
      state.message = null;
    },
  },
  extraReducers: (builder) => {
    // register
    builder.addCase(registerUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(registerUser.fulfilled, (state, action) => {
      state.loading = false;
      state.message = action.payload?.detail || action.payload?.message || 'Registration successful.';
    });
    builder.addCase(registerUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || action.error;
    });

    // login
    builder.addCase(loginUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginUser.fulfilled, (state, action) => {
      state.loading = false;
      state.access_token = action.payload?.access || action.payload?.access_token || state.access_token;
      state.refresh_token = action.payload?.refresh || action.payload?.refresh_token || state.refresh_token;
      state.user = action.payload?.user || state.user;
      state.message = action.payload?.detail || action.payload?.message || 'Login successful.';
      // persist tokens
      if (state.access_token) localStorage.setItem('access_token', state.access_token);
      if (state.refresh_token) localStorage.setItem('refresh_token', state.refresh_token);
    });
    builder.addCase(loginUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || action.error;
    });

    // verify email
    builder.addCase(verifyEmailUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(verifyEmailUser.fulfilled, (state, action) => {
      state.loading = false;
      state.message = action.payload?.detail || action.payload?.message || 'Email verified.';
    });
    builder.addCase(verifyEmailUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || action.error;
    });

    // resend verification
    builder.addCase(resendVerificationUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(resendVerificationUser.fulfilled, (state, action) => {
      state.loading = false;
      state.message = action.payload?.detail || action.payload?.message || 'Verification email resent.';
    });
    builder.addCase(resendVerificationUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || action.error;
    });

    // forgot password
    builder.addCase(forgotPasswordUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(forgotPasswordUser.fulfilled, (state, action) => {
      state.loading = false;
      state.message = action.payload?.detail || action.payload?.message || 'Password reset email sent.';
    });
    builder.addCase(forgotPasswordUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || action.error;
    });

    // reset password
    builder.addCase(resetPasswordUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(resetPasswordUser.fulfilled, (state, action) => {
      state.loading = false;
      state.message = action.payload?.detail || action.payload?.message || 'Password reset successful.';
    });
    builder.addCase(resetPasswordUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || action.error;
    });

    // change password
    builder.addCase(changePasswordUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(changePasswordUser.fulfilled, (state, action) => {
      state.loading = false;
      state.message = action.payload?.detail || action.payload?.message || 'Password changed successfully.';
    });
    builder.addCase(changePasswordUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || action.error;
    });

    // profile
    builder.addCase(getProfile.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getProfile.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload || state.user;
    });
    builder.addCase(getProfile.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || action.error;
    });

    builder.addCase(updateProfile.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateProfile.fulfilled, (state, action) => {
      state.loading = false;
      // replace user with backend response only
      state.user = action.payload || state.user;
      state.message = action.payload?.detail || action.payload?.message || null;
    });
    builder.addCase(updateProfile.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || action.error;
    });

    // logout
    builder.addCase(logoutUser.fulfilled, (state) => {
      state.user = null;
      state.access_token = null;
      state.refresh_token = null;
      state.message = 'Logged out';
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    });
  },
});

export const { clearError, clearMessage, setTokens, clearAuth } = authSlice.actions;

export default authSlice.reducer;
